/**
 * Account-security panel for the Super Admin (Platform Workspace only).
 * Rendered as the "Account" tab inside the platform Security hub.
 *
 * Surfaces, all hitting the gated /api/platform/account/* endpoints:
 *   - in-session change password
 *   - MFA: SMS phone enroll/verify, primary-email verify, secondary backup
 *     email, printed backup codes, enable/disable
 *   - IP hard-block toggle (with self-lockout guards on the server)
 *
 * After a break-glass recovery sign-in the login page redirects here with
 * ?addIp=<ip>; we show a one-click banner to allowlist that IP.
 */
import { useState, useEffect } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import {
  Shield, KeyRound, MessageSquare, Mail, MailPlus, Lock, Loader2,
  CheckCircle2, AlertTriangle, Copy, MapPin,
} from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Separator } from "@/components/ui/separator";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";

interface SecurityStatus {
  mfaEnabled: boolean;
  mfaDefaultMethod: string | null;
  phone: { present: boolean; verified: boolean; hint: string | null };
  primaryEmail: { address: string; verified: boolean };
  secondaryEmail: { address: string | null; verified: boolean };
  backupCodesRemaining: number;
  ipHardBlockEnabled: boolean;
}

const SECURITY_KEY = ["/api/platform/account/security"];

function VerifiedBadge({ ok }: { ok: boolean }) {
  return ok ? (
    <Badge className="bg-emerald-500 text-white"><CheckCircle2 className="h-3 w-3 mr-1" />Verified</Badge>
  ) : (
    <Badge variant="outline" className="text-muted-foreground">Not verified</Badge>
  );
}

export function AccountSecurityContent() {
  const { toast } = useToast();
  const { data: status, isLoading } = useQuery<SecurityStatus>({ queryKey: SECURITY_KEY });

  const refetchStatus = () => queryClient.invalidateQueries({ queryKey: SECURITY_KEY });

  // Change password
  const [curPw, setCurPw] = useState("");
  const [newPw, setNewPw] = useState("");
  const [confirmPw, setConfirmPw] = useState("");

  // Phone enroll
  const [phone, setPhone] = useState("");
  const [phoneStarted, setPhoneStarted] = useState(false);
  const [phoneCode, setPhoneCode] = useState("");

  // Primary email verify
  const [emailStarted, setEmailStarted] = useState(false);
  const [emailCode, setEmailCode] = useState("");

  // Secondary email
  const [secEmail, setSecEmail] = useState("");
  const [secStarted, setSecStarted] = useState(false);
  const [secCode, setSecCode] = useState("");

  // Backup codes (shown once)
  const [backupCodes, setBackupCodes] = useState<string[] | null>(null);

  // Disable MFA
  const [disablePw, setDisablePw] = useState("");

  // Recovered-IP banner
  const [recoveredIp, setRecoveredIp] = useState<string | null>(null);
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const ip = params.get("addIp");
    if (ip) setRecoveredIp(ip);
  }, []);

  const fail = (e: any, fallback: string) =>
    toast({ title: "Error", description: e?.message || fallback, variant: "destructive" });

  // ── Mutations ──────────────────────────────────────────────────────────────
  const changePw = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("POST", "/api/platform/account/change-password", {
        currentPassword: curPw, newPassword: newPw,
      });
      return res.json();
    },
    onSuccess: () => {
      toast({ title: "Password changed" });
      setCurPw(""); setNewPw(""); setConfirmPw("");
    },
    onError: (e) => fail(e, "Could not change password"),
  });

  const phoneStart = useMutation({
    mutationFn: async () => (await apiRequest("POST", "/api/platform/account/mfa/phone/start", { phone })).json(),
    onSuccess: () => { setPhoneStarted(true); toast({ title: "Code sent", description: "Check your phone for a 6-digit code." }); },
    onError: (e) => fail(e, "Could not send code"),
  });
  const phoneVerify = useMutation({
    mutationFn: async () => (await apiRequest("POST", "/api/platform/account/mfa/phone/verify", { code: phoneCode })).json(),
    onSuccess: () => { toast({ title: "Phone verified" }); setPhoneStarted(false); setPhoneCode(""); setPhone(""); refetchStatus(); },
    onError: (e) => fail(e, "Verification failed"),
  });

  const emailStart = useMutation({
    mutationFn: async () => (await apiRequest("POST", "/api/platform/account/mfa/email/start", {})).json(),
    onSuccess: () => { setEmailStarted(true); toast({ title: "Code sent", description: "Check your primary email." }); },
    onError: (e) => fail(e, "Could not send code"),
  });
  const emailVerify = useMutation({
    mutationFn: async () => (await apiRequest("POST", "/api/platform/account/mfa/email/verify", { code: emailCode })).json(),
    onSuccess: () => { toast({ title: "Primary email verified" }); setEmailStarted(false); setEmailCode(""); refetchStatus(); },
    onError: (e) => fail(e, "Verification failed"),
  });

  const secStart = useMutation({
    mutationFn: async () => (await apiRequest("POST", "/api/platform/account/mfa/secondary-email/start", { email: secEmail })).json(),
    onSuccess: () => { setSecStarted(true); toast({ title: "Code sent", description: "Check your secondary email." }); },
    onError: (e) => fail(e, "Could not send code"),
  });
  const secVerify = useMutation({
    mutationFn: async () => (await apiRequest("POST", "/api/platform/account/mfa/secondary-email/verify", { code: secCode })).json(),
    onSuccess: () => { toast({ title: "Secondary email verified" }); setSecStarted(false); setSecCode(""); setSecEmail(""); refetchStatus(); },
    onError: (e) => fail(e, "Verification failed"),
  });

  const regenCodes = useMutation({
    mutationFn: async () => (await apiRequest("POST", "/api/platform/account/mfa/backup-codes/regenerate", {})).json(),
    onSuccess: (d: { codes: string[] }) => { setBackupCodes(d.codes); toast({ title: "Backup codes generated", description: "Save them now — they won't be shown again." }); refetchStatus(); },
    onError: (e) => fail(e, "Could not generate codes"),
  });

  const enableMfa = useMutation({
    mutationFn: async () => (await apiRequest("POST", "/api/platform/account/mfa/enable", {})).json(),
    onSuccess: () => { toast({ title: "MFA enabled" }); refetchStatus(); },
    onError: (e) => fail(e, "Could not enable MFA"),
  });
  const disableMfa = useMutation({
    mutationFn: async () => (await apiRequest("POST", "/api/platform/account/mfa/disable", { currentPassword: disablePw })).json(),
    onSuccess: () => { toast({ title: "MFA disabled" }); setDisablePw(""); refetchStatus(); },
    onError: (e) => fail(e, "Could not disable MFA"),
  });

  const setHardBlock = useMutation({
    mutationFn: async (enabled: boolean) => (await apiRequest("POST", "/api/platform/security/ip-hard-block", { enabled })).json(),
    onSuccess: (d: { enabled: boolean }) => { toast({ title: d.enabled ? "IP hard-block ON" : "IP hard-block OFF" }); refetchStatus(); },
    onError: (e) => fail(e, "Could not update the hard-block"),
  });

  const addRecoveredIp = useMutation({
    mutationFn: async () => (await apiRequest("POST", "/api/platform/ip-allowlist", {
      cidr: `${recoveredIp}/32`, label: "Added after account recovery",
    })).json(),
    onSuccess: () => {
      toast({ title: "IP allowlisted", description: `${recoveredIp} can now sign in with a password.` });
      setRecoveredIp(null);
      const url = new URL(window.location.href);
      url.searchParams.delete("addIp");
      window.history.replaceState({}, "", url.toString());
      queryClient.invalidateQueries({ queryKey: ["/api/platform/ip-allowlist"] });
    },
    onError: (e) => fail(e, "Could not add IP"),
  });

  const copyCodes = () => {
    if (!backupCodes) return;
    navigator.clipboard.writeText(backupCodes.join("\n"));
    toast({ title: "Copied to clipboard" });
  };

  if (isLoading || !status) {
    return <div className="flex items-center justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>;
  }

  const pwMismatch = newPw.length > 0 && confirmPw.length > 0 && newPw !== confirmPw;
  const canEnableMfa = status.phone.verified && status.primaryEmail.verified && status.backupCodesRemaining > 0;

  return (
    <div className="space-y-6 max-w-3xl" data-testid="panel-account-security">
      {recoveredIp && (
        <Alert className="border-amber-400 bg-amber-50 dark:bg-amber-950/30">
          <MapPin className="h-4 w-4 text-amber-600" />
          <AlertTitle>You signed in via account recovery</AlertTitle>
          <AlertDescription className="flex items-center justify-between gap-3 flex-wrap">
            <span className="text-sm">Add <span className="font-mono font-medium">{recoveredIp}</span> to your IP allowlist so you can sign in with a password next time.</span>
            <Button size="sm" onClick={() => addRecoveredIp.mutate()} disabled={addRecoveredIp.isPending} data-testid="button-add-recovered-ip">
              {addRecoveredIp.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : "Add this IP"}
            </Button>
          </AlertDescription>
        </Alert>
      )}

      {/* MFA status summary */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Shield className="h-5 w-5" /> Multi-Factor Authentication
            {status.mfaEnabled
              ? <Badge className="bg-emerald-500 text-white ml-1">Enabled</Badge>
              : <Badge variant="outline" className="ml-1">Disabled</Badge>}
          </CardTitle>
          <CardDescription>SMS is the primary factor; email and backup codes are recovery factors.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="grid grid-cols-2 gap-3 text-sm">
            <div className="flex items-center justify-between rounded-md border p-2">
              <span className="flex items-center gap-2"><MessageSquare className="h-4 w-4" /> SMS {status.phone.hint ? `(${status.phone.hint})` : ""}</span>
              <VerifiedBadge ok={status.phone.verified} />
            </div>
            <div className="flex items-center justify-between rounded-md border p-2">
              <span className="flex items-center gap-2"><Mail className="h-4 w-4" /> Primary email</span>
              <VerifiedBadge ok={status.primaryEmail.verified} />
            </div>
            <div className="flex items-center justify-between rounded-md border p-2">
              <span className="flex items-center gap-2"><MailPlus className="h-4 w-4" /> Secondary email</span>
              <VerifiedBadge ok={status.secondaryEmail.verified} />
            </div>
            <div className="flex items-center justify-between rounded-md border p-2">
              <span className="flex items-center gap-2"><KeyRound className="h-4 w-4" /> Backup codes</span>
              <Badge variant={status.backupCodesRemaining > 0 ? "default" : "outline"}>{status.backupCodesRemaining} left</Badge>
            </div>
          </div>

          {!status.mfaEnabled ? (
            <div className="flex items-center gap-3">
              <Button onClick={() => enableMfa.mutate()} disabled={!canEnableMfa || enableMfa.isPending} data-testid="button-enable-mfa">
                {enableMfa.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : "Enable MFA"}
              </Button>
              {!canEnableMfa && (
                <p className="text-xs text-muted-foreground">Verify your phone, primary email, and generate backup codes first.</p>
              )}
            </div>
          ) : (
            <div className="flex items-end gap-2">
              <div className="space-y-1">
                <Label htmlFor="disable-pw" className="text-xs">Confirm password to disable</Label>
                <Input id="disable-pw" type="password" value={disablePw} onChange={(e) => setDisablePw(e.target.value)} className="h-9 w-64" data-testid="input-disable-mfa-password" />
              </div>
              <Button variant="destructive" onClick={() => disableMfa.mutate()} disabled={!disablePw || disableMfa.isPending} data-testid="button-disable-mfa">
                {disableMfa.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : "Disable MFA"}
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Enrollment cards */}
      <Card>
        <CardHeader><CardTitle className="text-base flex items-center gap-2"><MessageSquare className="h-4 w-4" /> SMS phone</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          {!phoneStarted ? (
            <div className="flex items-end gap-2">
              <div className="space-y-1 flex-1">
                <Label htmlFor="phone" className="text-xs">Phone number (E.164, e.g. +15551234567)</Label>
                <Input id="phone" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="+15551234567" data-testid="input-mfa-phone" />
              </div>
              <Button onClick={() => phoneStart.mutate()} disabled={phone.length < 5 || phoneStart.isPending} data-testid="button-mfa-phone-start">
                {phoneStart.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : "Send code"}
              </Button>
            </div>
          ) : (
            <div className="flex items-end gap-2">
              <div className="space-y-1 flex-1">
                <Label htmlFor="phone-code" className="text-xs">6-digit code</Label>
                <Input id="phone-code" inputMode="numeric" value={phoneCode} onChange={(e) => setPhoneCode(e.target.value.replace(/\s+/g, ""))} placeholder="123456" data-testid="input-mfa-phone-code" />
              </div>
              <Button onClick={() => phoneVerify.mutate()} disabled={phoneCode.length !== 6 || phoneVerify.isPending} data-testid="button-mfa-phone-verify">
                {phoneVerify.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : "Verify"}
              </Button>
              <Button variant="ghost" onClick={() => { setPhoneStarted(false); setPhoneCode(""); }}>Cancel</Button>
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle className="text-base flex items-center gap-2"><Mail className="h-4 w-4" /> Primary email — {status.primaryEmail.address}</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          {!emailStarted ? (
            <Button onClick={() => emailStart.mutate()} disabled={emailStart.isPending} data-testid="button-mfa-email-start">
              {emailStart.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : status.primaryEmail.verified ? "Re-verify email" : "Send verification code"}
            </Button>
          ) : (
            <div className="flex items-end gap-2">
              <div className="space-y-1 flex-1">
                <Label htmlFor="email-code" className="text-xs">6-digit code</Label>
                <Input id="email-code" inputMode="numeric" value={emailCode} onChange={(e) => setEmailCode(e.target.value.replace(/\s+/g, ""))} placeholder="123456" data-testid="input-mfa-email-code" />
              </div>
              <Button onClick={() => emailVerify.mutate()} disabled={emailCode.length !== 6 || emailVerify.isPending} data-testid="button-mfa-email-verify">
                {emailVerify.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : "Verify"}
              </Button>
              <Button variant="ghost" onClick={() => { setEmailStarted(false); setEmailCode(""); }}>Cancel</Button>
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2"><MailPlus className="h-4 w-4" /> Secondary backup email</CardTitle>
          <CardDescription>{status.secondaryEmail.address ? `Current: ${status.secondaryEmail.address}` : "A second email used as a recovery factor."}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          {!secStarted ? (
            <div className="flex items-end gap-2">
              <div className="space-y-1 flex-1">
                <Label htmlFor="sec-email" className="text-xs">Secondary email address</Label>
                <Input id="sec-email" type="email" value={secEmail} onChange={(e) => setSecEmail(e.target.value)} placeholder="backup@example.com" data-testid="input-secondary-email" />
              </div>
              <Button onClick={() => secStart.mutate()} disabled={!secEmail.includes("@") || secStart.isPending} data-testid="button-secondary-email-start">
                {secStart.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : "Send code"}
              </Button>
            </div>
          ) : (
            <div className="flex items-end gap-2">
              <div className="space-y-1 flex-1">
                <Label htmlFor="sec-code" className="text-xs">6-digit code</Label>
                <Input id="sec-code" inputMode="numeric" value={secCode} onChange={(e) => setSecCode(e.target.value.replace(/\s+/g, ""))} placeholder="123456" data-testid="input-secondary-email-code" />
              </div>
              <Button onClick={() => secVerify.mutate()} disabled={secCode.length !== 6 || secVerify.isPending} data-testid="button-secondary-email-verify">
                {secVerify.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : "Verify"}
              </Button>
              <Button variant="ghost" onClick={() => { setSecStarted(false); setSecCode(""); }}>Cancel</Button>
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2"><KeyRound className="h-4 w-4" /> Backup codes</CardTitle>
          <CardDescription>Single-use printed codes. Regenerating invalidates any previous set.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <Button onClick={() => regenCodes.mutate()} disabled={regenCodes.isPending} data-testid="button-regen-backup-codes">
            {regenCodes.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : status.backupCodesRemaining > 0 ? "Regenerate backup codes" : "Generate backup codes"}
          </Button>
          {backupCodes && (
            <Alert className="border-amber-400 bg-amber-50 dark:bg-amber-950/30">
              <AlertTriangle className="h-4 w-4 text-amber-600" />
              <AlertTitle className="flex items-center justify-between">
                Save these now — they won't be shown again
                <Button size="sm" variant="ghost" onClick={copyCodes} data-testid="button-copy-backup-codes"><Copy className="h-3 w-3 mr-1" />Copy</Button>
              </AlertTitle>
              <AlertDescription>
                <div className="grid grid-cols-2 gap-1 font-mono text-sm mt-2" data-testid="list-backup-codes">
                  {backupCodes.map((c) => <div key={c}>{c}</div>)}
                </div>
              </AlertDescription>
            </Alert>
          )}
        </CardContent>
      </Card>

      <Separator />

      {/* Change password */}
      <Card>
        <CardHeader><CardTitle className="flex items-center gap-2"><Lock className="h-5 w-5" /> Change password</CardTitle></CardHeader>
        <CardContent className="space-y-3 max-w-sm">
          <div className="space-y-1">
            <Label htmlFor="cur-pw" className="text-xs">Current password</Label>
            <Input id="cur-pw" type="password" value={curPw} onChange={(e) => setCurPw(e.target.value)} data-testid="input-current-password" />
          </div>
          <div className="space-y-1">
            <Label htmlFor="new-pw" className="text-xs">New password (min 12 chars)</Label>
            <Input id="new-pw" type="password" value={newPw} onChange={(e) => setNewPw(e.target.value)} data-testid="input-new-password" />
          </div>
          <div className="space-y-1">
            <Label htmlFor="confirm-pw" className="text-xs">Confirm new password</Label>
            <Input id="confirm-pw" type="password" value={confirmPw} onChange={(e) => setConfirmPw(e.target.value)} data-testid="input-confirm-password" />
            {pwMismatch && <p className="text-xs text-destructive">Passwords don't match.</p>}
          </div>
          <Button
            onClick={() => changePw.mutate()}
            disabled={!curPw || newPw.length < 12 || pwMismatch || changePw.isPending}
            data-testid="button-change-password"
          >
            {changePw.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : "Change password"}
          </Button>
        </CardContent>
      </Card>

      {/* IP hard-block */}
      <Card>
        <CardHeader><CardTitle className="flex items-center gap-2"><MapPin className="h-5 w-5" /> IP hard-block</CardTitle></CardHeader>
        <CardContent>
          <div className="flex items-start justify-between gap-4">
            <div className="space-y-1">
              <p className="text-sm font-medium">Refuse password sign-in from non-allowlisted IPs</p>
              <p className="text-xs text-muted-foreground">
                When ON, a correct password + MFA is <strong>not</strong> enough from an un-allowlisted network —
                you must use account recovery. Keep OFF until you've tested recovery.
              </p>
            </div>
            <Switch
              checked={status.ipHardBlockEnabled}
              disabled={setHardBlock.isPending}
              onCheckedChange={(v) => setHardBlock.mutate(v)}
              data-testid="switch-ip-hard-block"
            />
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
